import type { Conciliacao, ExplicacaoDoCaso, NumerosDaDivergencia } from '../../auditoria';

/**
 * OS TRÊS NÚMEROS DA DIVERGÊNCIA (dono/Carlos, 14/10/2026). Nunca misturar:
 *   1. Falta na reposição — a tarefa do balconista (pediu − achou).
 *   2. Ainda não comprovado — o que o HOS diz que estaria no estoque e a busca não comprovou.
 *      Pendência de verificação, NUNCA perda.
 *   3. Divergência real — só depois da prateleira + estoque inteiro conciliados com o HOS.
 * Os números vêm prontos do servidor (motor no domínio); aqui só se mostram.
 */

const un = (n: number) => `${String(n)} ${n === 1 ? 'unidade' : 'unidades'}`;
const sinal = (n: number) => (n < 0 ? '−' : n > 0 ? '+' : '');

/** Linha compacta do card da Central: os dados de partida e os dois primeiros números. */
export function NumerosEmLinha({ n }: { n: NumerosDaDivergencia }) {
  if (n.tipo === 'ESTOQUE_INCONSISTENTE')
    return (
      <div className="dp-tn-linha">
        <span>
          Prateleira <b>{n.prateleira ?? '—'}</b>
        </span>
        <span>
          HOS <b>{n.hosNaContagem ?? '—'}</b>
        </span>
        <span className="dp-tn-chip dp-tn-chip-hos">
          {n.acimaDoHos > 0 ? `+${String(n.acimaDoHos)} acima do HOS` : 'Prateleira acima do HOS'}
        </span>
        <span className="dp-tn-chip">Sem reposição pedida</span>
        {n.metodo === 'ANTERIOR' && <span className="dp-tn-chip">Método anterior</span>}
      </div>
    );
  return (
    <div className="dp-tn-linha">
      <span>
        HOS <b>{n.hosNaContagem ?? '—'}</b>
      </span>
      <span>
        Prateleira <b>{n.prateleira ?? '—'}</b>
      </span>
      {n.esperadoNoEstoque !== null && (
        <span>
          Esperado no estoque <b>{n.esperadoNoEstoque}</b>
        </span>
      )}
      <span>
        Pediu <b>{n.solicitado}</b> · achou <b>{n.localizado ?? 0}</b>
      </span>
      <span
        className={`dp-tn-chip${n.faltaNaReposicao > 0 ? ' dp-tn-chip-falta' : ''}`}
        title="Falta na reposição: a tarefa (pediu − achou). Não é perda de estoque."
      >
        {n.faltaNaReposicao > 0
          ? `Falta na reposição ${String(n.faltaNaReposicao)}`
          : 'Reposição atendida'}
      </span>
      {n.naoComprovado !== null && n.naoComprovado > 0 && (
        <span
          className="dp-tn-chip"
          title="Ainda não comprovado pela busca: pendência de verificação, não é perda."
        >
          Não comprovado {n.naoComprovado}
        </span>
      )}
      {n.metodo === 'ANTERIOR' && <span className="dp-tn-chip">Método anterior</span>}
    </div>
  );
}

function Grande({
  n,
  rotulo,
  valor,
  vazio,
  nota,
  tom,
}: {
  n: string;
  rotulo: string;
  valor: string | null;
  vazio: string;
  nota: string;
  tom?: 'falta' | 'real' | 'neutro';
}) {
  return (
    <div className={`dp-tn-card dp-tn-${tom ?? 'neutro'}`}>
      <span className="dp-tn-rotulo">
        <span className="dp-tn-n">{n}</span>
        {rotulo}
      </span>
      {valor === null ? (
        <span className="dp-tn-vazio">{vazio}</span>
      ) : (
        <strong className="dp-tn-valor">{valor}</strong>
      )}
      <span className="dp-tn-nota">{nota}</span>
    </div>
  );
}

/** A versão grande da análise do caso: partida, os três números e a explicação. */
export function TresNumeros({
  n,
  conc,
  explicacao,
}: {
  n: NumerosDaDivergencia;
  conc: Conciliacao | null;
  explicacao: ExplicacaoDoCaso | null;
}) {
  const inconsistente = n.tipo === 'ESTOQUE_INCONSISTENTE';
  const real =
    conc && conc.diferenca && conc.confianca !== 'AGUARDANDO' && conc.confianca !== 'INCONCLUSIVA'
      ? conc.diferenca.min === conc.diferenca.max
        ? `${sinal(conc.diferenca.min)}${un(Math.abs(conc.diferenca.min))}`
        : `${sinal(conc.diferenca.min)}${String(Math.abs(conc.diferenca.min))} a ${sinal(conc.diferenca.max)}${String(Math.abs(conc.diferenca.max))}`
      : null;
  const notaReal = !conc
    ? 'Lendo a investigação…'
    : conc.confianca === 'AGUARDANDO'
      ? 'Aguardando prateleira e estoque inteiro'
      : conc.confianca === 'INCONCLUSIVA'
        ? 'Inconclusiva: faltam dados do HOS'
        : conc.confianca === 'CONFIAVEL'
          ? 'Confirmada: físico × HOS no mesmo instante'
          : 'Aproximada: veja as ressalvas';
  return (
    <div className="dp-tn">
      <dl className="dp-tn-partida">
        <div>
          <dt>HOS na contagem</dt>
          <dd>{n.hosNaContagem === null ? 'sem leitura' : un(n.hosNaContagem)}</dd>
        </div>
        <div>
          <dt>Prateleira (balconista)</dt>
          <dd>{n.prateleira === null ? '—' : un(n.prateleira)}</dd>
        </div>
        {inconsistente ? (
          <div>
            <dt>Acima do HOS</dt>
            <dd>{un(n.acimaDoHos)}</dd>
          </div>
        ) : (
          <>
            <div>
              <dt>Esperado no estoque</dt>
              <dd>{n.esperadoNoEstoque === null ? '—' : un(n.esperadoNoEstoque)}</dd>
            </div>
            <div>
              <dt>Pedido para repor</dt>
              <dd>{un(n.solicitado)}</dd>
            </div>
            <div>
              <dt>Achou na reposição</dt>
              <dd>
                {un(n.localizado ?? 0)}
                {n.reposto !== null && n.reposto !== n.localizado && (
                  <span className="dp-tn-sub"> · repôs {n.reposto}</span>
                )}
              </dd>
            </div>
          </>
        )}
      </dl>

      <div className="dp-tn-grade">
        <Grande
          n="1"
          rotulo="Falta na reposição"
          valor={inconsistente ? null : un(n.faltaNaReposicao)}
          vazio="Nenhuma reposição pedida"
          nota={
            inconsistente
              ? 'A prateleira já tinha mais que o HOS'
              : n.faltaNaReposicao > 0
                ? 'Mede a tarefa, não a perda'
                : 'Reposição atendida'
          }
          tom={n.faltaNaReposicao > 0 ? 'falta' : 'neutro'}
        />
        <Grande
          n="2"
          rotulo={inconsistente ? 'HOS abaixo da prateleira' : 'Ainda não comprovado'}
          valor={
            inconsistente ? un(n.acimaDoHos) : n.naoComprovado === null ? null : un(n.naoComprovado)
          }
          vazio="Sem leitura do HOS"
          nota={
            inconsistente
              ? 'Pergunta sobre o saldo, não sobre quem contou'
              : n.localizouAlemDoEsperado
                ? 'Achou mais do que o HOS dizia'
                : 'Pendência de verificação — não é perda'
          }
        />
        <Grande
          n="3"
          rotulo="Divergência real"
          valor={real}
          vazio={conc?.confianca === 'INCONCLUSIVA' ? 'Inconclusiva' : 'Ainda não'}
          nota={notaReal}
          tom={real ? 'real' : 'neutro'}
        />
      </div>

      {explicacao && explicacao.linhas.length > 0 && (
        <div className="dp-tn-explica">
          {explicacao.linhas.map((l) => (
            <p key={l}>{l}</p>
          ))}
          {explicacao.conclusao && (
            <p className="dp-tn-conclusao" data-confirmada={explicacao.confirmada}>
              {explicacao.conclusao}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
